import React, { useEffect, useMemo, useState } from 'react';
import { Container, Row, Col, Spinner, Alert, Form, Button } from 'react-bootstrap';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import HelpSidebar from '../components/help/HelpSidebar';
import HelpContent from '../components/help/HelpContent';
import HelpBreadcrumb from '../components/help/HelpBreadcrumb';
import DashboardLayout from '../components/layout/DashboardLayout';
import './HelpPage.css';

const HelpPage = () => {
  const { t, i18n } = useTranslation();
  const { profile } = useAuth();
  const { moduleId } = useParams();
  const navigate = useNavigate();

  const [manifestStatus, setManifestStatus] = useState('loading');
  const [manifest, setManifest] = useState(null);
  // Bumping this re-dispara el fetch del manifiesto — el botón "Reintentar".
  const [reloadToken, setReloadToken] = useState(0);

  const locale = useMemo(() => {
    const lang = (i18n?.language || 'es').toLowerCase();
    return lang.startsWith('en') ? 'en' : 'es';
  }, [i18n?.language]);

  const role = profile?.role || 'client';

  useEffect(() => {
    let cancelled = false;
    setManifestStatus('loading');
    const url = `${process.env.PUBLIC_URL || ''}/help/manifest.json`;
    fetch(url, { cache: 'no-store' })
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then((data) => {
        if (!cancelled) {
          setManifest(data);
          setManifestStatus('ready');
        }
      })
      .catch((err) => {
        console.error('[HelpPage] failed to load manifest', err);
        if (!cancelled) setManifestStatus('error');
      });
    return () => {
      cancelled = true;
    };
  }, [reloadToken]);

  const visibleModules = useMemo(() => {
    if (!manifest?.modules) return [];
    return manifest.modules
      .filter((m) => Array.isArray(m.roles) && m.roles.includes(role))
      .slice()
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  }, [manifest, role]);

  const defaultModuleId = manifest?.defaultModule || 'index';
  const activeModuleId = moduleId || defaultModuleId;

  const activeModule = useMemo(
    () => visibleModules.find((m) => m.id === activeModuleId) || null,
    [visibleModules, activeModuleId]
  );

  const moduleIsVisible = !!activeModule;
  const moduleTitle = activeModule?.titles?.[locale] || activeModule?.titles?.es || null;

  // Las dos ramas de abajo salían SIN <DashboardLayout>, así que mientras el
  // manifiesto cargaba (o fallaba) el usuario perdía el cromo entero de la
  // página — encabezado, fondo de sección y navegación — y quedaba con un
  // spinner suelto sobre fondo blanco, sin forma de irse a otro lado.
  if (manifestStatus === 'loading') {
    return (
      <DashboardLayout>
        <Container className="py-5 text-center">
          <Spinner animation="border" role="status" variant="primary">
            <span className="visually-hidden">{t('common.loading', 'Cargando...')}</span>
          </Spinner>
        </Container>
      </DashboardLayout>
    );
  }

  if (manifestStatus === 'error') {
    return (
      <DashboardLayout>
        <Container className="py-4">
          <Alert variant="warning" data-testid="help-manifest-error">
            <Alert.Heading>
              <i className="bi bi-exclamation-triangle me-2" aria-hidden="true"></i>
              {t('help.manifestErrorTitle', 'No pudimos cargar el índice del manual')}
            </Alert.Heading>
            <p className="mb-3">
              {t('help.manifestError', 'Intenta recargar la página. Si el problema persiste, contacta al administrador.')}
            </p>
            <Button
              variant="outline-warning"
              size="sm"
              onClick={() => setReloadToken((n) => n + 1)}
              data-testid="help-manifest-retry"
            >
              <i className="bi bi-arrow-clockwise me-1" aria-hidden="true"></i>
              {t('common.retry', 'Reintentar')}
            </Button>
          </Alert>
        </Container>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="help-page">
      <Container fluid className="py-3">
        <div className="help-header">
          <div>
            <h1 className="help-title">
              <i className="bi bi-question-circle me-2" aria-hidden="true"></i>
              {t('help.title', 'Ayuda')}
            </h1>
            <p className="help-subtitle mb-0">
              {t('help.subtitle', 'Guía de uso de la plataforma, organizada por módulo.')}
            </p>
          </div>
        </div>

        {/* Mobile: module selector */}
        <div className="help-mobile-picker d-md-none">
          <Form.Select
            aria-label={t('help.pickerLabel', 'Elige un módulo')}
            value={activeModuleId}
            onChange={(e) => navigate(`/ayuda/${e.target.value}`)}
          >
            {visibleModules.map((m) => (
              <option key={m.id} value={m.id}>
                {m.titles?.[locale] || m.titles?.es || m.id}
              </option>
            ))}
          </Form.Select>
        </div>

        <Row className="help-layout">
          {/* Desktop: sidebar */}
          <Col md={3} lg={3} className="help-sidebar-col d-none d-md-block">
            <HelpSidebar
              modules={visibleModules}
              role={role}
              locale={locale}
              activeModuleId={activeModuleId}
            />
          </Col>

          <Col xs={12} md={9} lg={9} className="help-content-col">
            <HelpBreadcrumb moduleTitle={moduleTitle} />
            {moduleIsVisible ? (
              <HelpContent moduleId={activeModuleId} locale={locale} />
            ) : (
              <Alert variant="info" className="mt-3" data-testid="help-module-not-found">
                <Alert.Heading>
                  <i className="bi bi-info-circle me-2" aria-hidden="true"></i>
                  {t('help.notFoundTitle', 'Módulo no encontrado')}
                </Alert.Heading>
                <p className="mb-0">
                  {t(
                    'help.notFound',
                    'Este módulo no existe o tu rol no tiene acceso. Elige otro desde la navegación.'
                  )}
                </p>
              </Alert>
            )}
          </Col>
        </Row>
      </Container>
      </div>
    </DashboardLayout>
  );
};

export default HelpPage;
